import React, { useRef, DragEvent, PropsWithChildren, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect, useSelector } from 'react-redux';
import { IDT } from '@ff-os/pdp-shared-redux/IDT';
import { ButtonWithIcon } from '@ff-os/shared-react/components/buttons/button-with-icon/ButtonWithIcon';
import { Button } from '@ff-os/shared-react/components/buttons/button/Button';
import { Cell } from '@ff-os/shared-react/components/grid/Cell';
import { Grid } from '@ff-os/shared-react/components/grid/Grid';
import { Icon } from '@ff-os/shared-react/components/icons/Icon';
import { Heading } from '@ff-os/shared-react/components/panel/Heading';
import { Panel } from '@ff-os/shared-react/components/panel/Panel';
import { PriceLabel } from '@ff-os/shared-react/components/price-label/PriceLabel';
import { useExternalFromPriceRender } from '@ff-os/shared-react/hooks/useExternalFromPriceRender';
import { useScrollIntoView } from '@ff-os/shared-react/hooks/useScrollIntoView';
import { selectAccessorySizes, selectSizeById } from '@ff-scene-plugins/photo-prints-redux/selectors/sizeSelectors';
import { selectIsPhotoFunProcessing, selectCanHaveBorderDraggedInto, selectHasMagicBorderApplied, selectMagicBorderAutoAppliedTimestamp } from '@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice';
import {
    selectFailedUploadsCount,
    selectHasVariantUpdated,
    selectIsNewGroup,
    selectSuccessfulUploadsCount
} from '@ff-scene-plugins/photo-prints-redux/slices/groupsUiSlice';
import {
    hasValidAccessoryQuantityByGroupId,
    getAccessoryValidationByGroupId,
    selectPhotoIdsByGroupId
} from '@ff-scene-plugins/photo-prints-redux/slices/photosSlice';
import { PhotoPrintsPdpDispatch, PhotoPrintsPdpState } from '@ff-scene-plugins/photo-prints-redux/store';
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from 'classnames';
import { selectBorderOptions } from '../../../_redux-borders';
import { selectActiveVariant, selectCanRemoveGroup, selectIsEmptyGroup } from '../../../_redux-groups';
import {
    selectFrameId,
    selectAccessoryId,
    setFrameId,
    setAccessoryId
} from '../../../_redux-ui';
import { selectVariantByGroupId } from '../../../_redux-variants';
import { createFaceDetectionWebWorker } from '../../../helpers/loadFaceDetectionWebWorker';
import { applyAccessoryThunk } from '../../../thunks/applyAccessoryThunk';
import { applyMagicBorderThunk } from '../../../thunks/applyMagicBorderThunk';
import { removeGroup } from '../../../thunks/removeGroup';
import { updateVariant } from '../../../thunks/updateVariant';
import { VariantOption } from '../../../VariantOption';
import { DropMagicFrameAnimation } from '../../fotofun/components';
import { PhotoPrintOptionGroupTitle } from './PhotoPrintOptionGroupTitle';
import { VariantOptionGroup } from './variant-option-group/VariantOptionGroup';
import styles from './PhotoPrintGroupPanel.module.scss';

const RequestPriceLabel = ({ priceRequest }: { priceRequest: IDT.FromPriceRequest }) => {
    const { t, i18n } = useTranslation();
    const { destinationRef, plainText, object } = useExternalFromPriceRender(priceRequest);

    const priceText = object
        ? object.price.toLocaleString(i18n.language, { currency: object.currency, style: 'currency' })
        : '';

    return (
        <span style={{ marginLeft: 'auto' }} ref={destinationRef}>
            {plainText && <PriceLabel className={styles.label}>{plainText} {t('photo_prints.price_apiece')}</PriceLabel>}
            {object && <PriceLabel className={styles.label}>{priceText} {t('photo_prints.price_apiece')}</PriceLabel>}
        </span>
    );
};

const _PhotoPrintGroupPanel = ({
    groupId, browse, dragging, children,
    isEmpty, canRemove, activeVariant,
    successfulUploadsCount, failedUploadsCount,
    isNewGroup, hasVariantUpdated, updateVariant,
    remove, canHaveBorderDraggedInto, applyMagicBorder,
    isPhotoFunProcessing, hasMagicBorderApplied,
    hasAccessoryApplied, applyAccessory,
    isUploading,
    hasValidAcccessoryQuantities, getAccessoryValidation,
    frameId, accessoryId,
    hasActiveClickToApply,
    clearFrameId, clearAccessoryId,
    magicBorderAutoAppliedTimestamp,
    selectPhotoIdsById,
    openAccessories,
    borderOptions
}: Props) => {

    const { t } = useTranslation();

    const ref = useRef<HTMLDivElement>(null);
    const scrollIntoView = useScrollIntoView(ref, { includeHidden: true });

    const [hasBorderDraggedOver, setHasBorderDraggedOver] = useState(false);
    const [magicBorderIsApplied, setMagicBorderIsApplied] = useState(false);
    const [hasMagicBorderDraggedOver, setHasMagicBorderDraggedOver] = useState(false);
    const [hasTriggeredPhotoProcessing, setHasTriggeredPhotoProcessing] = useState(false);
    const [borderJustDropped, setBorderJustDropped] = useState(false);
    const [magicBorderJustDropped, setMagicBorderJustDropped] = useState(false);
    const [showGradientAfterDrop, setShowGradientAfterDrop] = useState(false);
    const [showSequentialPopAnimation, setShowSequentialPopAnimation] = useState(false);
    const [isVariantCollapsableOpen, setIsVariantCollapsableOpen] = useState(isEmpty);
    const [showInitialPopIn, setShowInitialPopIn] = useState(false);
    const [showPulseAttention, setShowPulseAttention] = useState(false);
    const hasShownInitialPopIn = useRef(false);
    const isUploadingRef = useRef<boolean>(!!isUploading);
    const dragLeaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const isSuccessMessage = successfulUploadsCount > 0 || isNewGroup || hasVariantUpdated;
    const isErrorMessage = failedUploadsCount > 0;
    const hasMessage = isSuccessMessage || isErrorMessage;

    const accessoryValidation = getAccessoryValidation ?? null;
    const accessories = useSelector((state: PhotoPrintsPdpState) => selectAccessorySizes(state));

    const isMountedRef = useRef(true);

    useEffect(() => {

        isMountedRef.current = true;

        return () => {

            isMountedRef.current = false;

            if (dragLeaveTimeoutRef.current) {
                clearTimeout(dragLeaveTimeoutRef.current);
            }
        };
    }, []);


    useEffect(() => {
        let prevUploadingRef = isUploadingRef.current;

        if (!prevUploadingRef && isUploading) {
            setIsVariantCollapsableOpen(false);
        }

        isUploadingRef.current = !!isUploading;
    }, [isUploading]);


    useEffect(() => {

        if (hasMessage) {
            scrollIntoView();
        }

        createFaceDetectionWebWorker().catch(console.error);
    }, [hasMessage, scrollIntoView]);

    useEffect(() => {

        if (!isPhotoFunProcessing) {
            setHasTriggeredPhotoProcessing(false);
        }

    }, [isPhotoFunProcessing]);

    // Trigger initial pop-in animation when photos are first rendered
    useEffect(() => {
        if (!hasShownInitialPopIn.current && selectPhotoIdsById.length > 0 && !isEmpty) {
            hasShownInitialPopIn.current = true;
            setShowInitialPopIn(true);

            // Remove the class after animation completes
            setTimeout(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setShowInitialPopIn(false);
            }, 400); // Match animation duration
        }
    }, [selectPhotoIdsById.length, isEmpty]);

    // Listen for automatically applied magic borders and trigger the same visual effects as manual drops
    useEffect(() => {
        if (magicBorderAutoAppliedTimestamp) {
            // Trigger the same animations as when a magic border is manually dropped
            setMagicBorderIsApplied(true);
            setHasTriggeredPhotoProcessing(true);

            // Show shake animation for any border drop
            setBorderJustDropped(true);
            setTimeout(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setBorderJustDropped(false);
            }, 400);

            // Show magic border specific effects
            setMagicBorderJustDropped(true);
            setShowGradientAfterDrop(true);
            setShowSequentialPopAnimation(true);

            // Remove gradient after 400ms
            setTimeout(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setMagicBorderJustDropped(false);
                setShowGradientAfterDrop(false);
            }, 400);

            // Keep sequential pop animation running longer
            setTimeout(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setShowSequentialPopAnimation(false);
            }, 1200);
        }
    }, [magicBorderAutoAppliedTimestamp]);

    // Listen for pulse trigger from order button
    useEffect(() => {
        if (!ref.current) return;

        const element = ref.current;
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const target = mutation.target as HTMLElement;
                    if (target.classList.contains('pulse-attention-trigger')) {
                        setShowPulseAttention(true);
                        setTimeout(() => {
                            if (!isMountedRef.current) return;
                            setShowPulseAttention(false);
                        }, 400); // Match animation duration
                    }
                }
            });
        });

        observer.observe(element, {
            attributes: true,
            attributeFilter: ['class']
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    useEffect(() => {
        return () => {
            if (dragLeaveTimeoutRef.current) {
                clearTimeout(dragLeaveTimeoutRef.current);
            }
        };
    }, []);

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {

        const isMagicEffect = e.dataTransfer.types.includes('ismagiceffect');
        setHasMagicBorderDraggedOver(isMagicEffect);

        if (!hasBorderDraggedOver && (
            e.dataTransfer.types.includes('accessoryid')
            || e.dataTransfer.types.includes('frameid'))) {

            e.preventDefault();
            setHasBorderDraggedOver(true);
        }


        if (dragLeaveTimeoutRef.current) {
            clearTimeout(dragLeaveTimeoutRef.current);
            dragLeaveTimeoutRef.current = null;
        }
    };

    const handleFrames = (e: DragEvent<HTMLDivElement>, frameId: string) => {

        const draggedEl = document.querySelector(`[data-id="${frameId}"]`) as HTMLElement;

        if (draggedEl && ref.current) {
            DropMagicFrameAnimation(draggedEl, ref.current, { x: e.clientX, y: e.clientY });
        }

        updateVariant(activeVariant.paperId, activeVariant.sizeId, "fotofun-border");

        applyMagicBorder(frameId);
        setMagicBorderIsApplied(true);
        setHasTriggeredPhotoProcessing(true);

        // Always show shake animation for any border drop
        setBorderJustDropped(true);
        setTimeout(() => {

            if (!isMountedRef.current) {
                return;
            }

            setBorderJustDropped(false);
        }, 400); // Match the shake animation duration

        // Only show gradient effects for magic borders
        const isMagicBorder = e.dataTransfer?.types?.includes('ismagiceffect');
        if (isMagicBorder) {
            setMagicBorderJustDropped(true);
            setShowGradientAfterDrop(true);
            setShowSequentialPopAnimation(true);

            // Remove gradient after 400ms
            setTimeout(() => {

                if (!isMountedRef.current) {
                    return;
                }

                setMagicBorderJustDropped(false);
                setShowGradientAfterDrop(false);
            }, 400);

            // Keep sequential pop animation running longer to allow full staggered effect
            setTimeout(() => {

                if (!isMountedRef.current) {
                    return;
                }

                setShowSequentialPopAnimation(false);
            }, 1200); // Allow time for all images to complete their animation cycle
        }
    };

    const handleAccessories = (accessoryId: string) => {

        const selectedAccessory = accessories.find(acc => acc.id === accessoryId);
        const minimumQuantity = selectedAccessory?.minimumQuantity || 1;
        const photoCount = selectPhotoIdsById.length;

        if ((minimumQuantity === 1 && photoCount > 1) ||
            (minimumQuantity > 1 && photoCount >= minimumQuantity)) {
            openAccessories(accessoryId);
        } else {
            applyAccessory(accessoryId);
        }
    };

    const handleDragLeave = () => {

        if (dragLeaveTimeoutRef.current) {
            clearTimeout(dragLeaveTimeoutRef.current);
        }

        dragLeaveTimeoutRef.current = setTimeout(() => {

            if (!isMountedRef.current) {
                return;
            }

            setHasBorderDraggedOver(false);
        }, 50);
    };

    const handleBorderDrop = (e: DragEvent<HTMLDivElement>) => {

        const accessoryId = e.dataTransfer.getData('accessoryId');
        const frameId = e.dataTransfer.getData('frameId');

        if (accessoryId) {
            handleAccessories(accessoryId);
        }

        if (frameId) {
            handleFrames(e, frameId);
        }

        // Clear any pending timeout and immediately set dragged over to false
        if (dragLeaveTimeoutRef.current) {
            clearTimeout(dragLeaveTimeoutRef.current);
            dragLeaveTimeoutRef.current = null;
        }
        setHasBorderDraggedOver(false);
    };

    const handleClickToApply = (e: React.MouseEvent<HTMLDivElement>) => {

        if (frameId) {
            handleFrames(e as any, frameId);
            clearFrameId();
        } else if (accessoryId) {
            handleAccessories(accessoryId);
            clearAccessoryId();
        }
    };

    const removeMagicBorder = () => {
        applyMagicBorder(null);
        // Filter for available border options and use the first one
        const availableBorderOptions = borderOptions.filter(option => option.available);
        const firstBorderOption = availableBorderOptions.length > 0 ? availableBorderOptions[0].value : "without-border";
        updateVariant(activeVariant.paperId, activeVariant.sizeId, firstBorderOption);
        setMagicBorderIsApplied(false);
    };

    const toggleVariantCollapsable = () => {
        setIsVariantCollapsableOpen(!isVariantCollapsableOpen);
    };

    const shouldShowRainbowGradient = (): boolean => {

        if (showGradientAfterDrop) {
            return true;
        }

        if (!hasMagicBorderDraggedOver) {
            return false;
        }

        if (hasMagicBorderDraggedOver && hasBorderDraggedOver) {
            return true;
        }

        if (isPhotoFunProcessing && hasTriggeredPhotoProcessing) {
            return true;
        }

        return false;
    };

    const panelClassName = classNames(styles.panel, {
        [styles.isEmpty]: isEmpty,
        [styles.success]: !dragging && isSuccessMessage,
        [styles.error]: (!dragging && isErrorMessage),
        [styles.rainbowGradient]: shouldShowRainbowGradient(),
        [styles.rainbowBorder]: canHaveBorderDraggedInto,
        [styles.rainbowBorderActive]: hasBorderDraggedOver,
        [styles.borderDropped]: borderJustDropped,
        [styles.magicBorderDropped]: magicBorderJustDropped,
        [styles.sequentialPopAnimation]: showSequentialPopAnimation,
        [styles.errorBorder]: !hasValidAcccessoryQuantities,
        [styles.initialPopIn]: showInitialPopIn,
        [styles.pulseAttention]: showPulseAttention
    });

    const collapsableContainerClassName = classNames(styles.collapsableContainer, {
        [styles.expanded]: isVariantCollapsableOpen
    });

    const buttonTitle = () => {
        if (accessoryId) {
            const selectedAccessory = accessories.find(acc => acc.id === accessoryId);
            const minimumQuantity = selectedAccessory?.minimumQuantity || 1;
            return minimumQuantity > 1
                ? t('photo_prints.click_to_select_photos')
                : t('photo_prints.click_to_apply');
        }
        return t('photo_prints.click_to_apply');
    };

    return (
        <Panel
            className={panelClassName}
            ref={ref}
            data-group-id={groupId}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleBorderDrop}
        >
            <span className={styles.rainbowBackground} />

            {hasActiveClickToApply && !isEmpty && (() => {
                // Hide group overlay for single accessories - show photo-level overlays instead
                if (accessoryId) {
                    const selectedAccessory = accessories.find(acc => acc.id === accessoryId);
                    const isSingleAccessory = !selectedAccessory?.minimumQuantity || selectedAccessory.minimumQuantity === 1;
                    if (isSingleAccessory) {
                        return null;
                    }
                }

                return (
                    <div className={styles.clickToApplyOverlay} onClick={handleClickToApply}>
                        <Button>
                            {buttonTitle()}
                        </Button>
                    </div>
                );
            })()}
            <Grid direction="column" gap="s" fullHeight fullWidth>
                <Cell className={styles.cell}>
                    <Heading className={styles.heading} icon="shopping-cart">
                        <PhotoPrintOptionGroupTitle
                            groupId={groupId}
                            sizeId={activeVariant.sizeId}
                            hasAccessoryApplied={hasAccessoryApplied}
                            onClick={toggleVariantCollapsable}
                        />
                        <RequestPriceLabel priceRequest={{ sku: activeVariant.sku }} />
                    </Heading>
                </Cell>

                {!hasAccessoryApplied && (
                    <Cell className={styles.cell}>
                        <div className={collapsableContainerClassName}>
                            <div className={styles.collapsableContent}>
                                <VariantOptionGroup
                                    activeVariant={activeVariant}
                                    groupId={groupId}
                                    isVariantCollapsableOpen={isVariantCollapsableOpen}
                                    setIsVariantCollapsableOpen={setIsVariantCollapsableOpen}
                                />
                            </div>
                        </div>
                    </Cell>
                )}

                {!hasValidAcccessoryQuantities && accessoryValidation && (
                    <Cell className={classNames(styles.cell, styles.message)}>
                        <p className={styles.quantityErrorMessage}>
                            <Icon type="alert-circle-info" strokeWidth="1.5" size="small" />
                            {t('photo_prints.accessory_quantity_errored', {
                                minimum: accessoryValidation.minimumRequired,
                                shortage: accessoryValidation.shortage})}
                        </p>
                    </Cell>
                )}

                <Cell className={styles.photosCell}>{children}</Cell>

                {hasMessage ? (
                    <Cell className={classNames(styles.messagesCell, styles.cell, styles.message)}>
                        <ul className={styles.messages}>
                            {hasVariantUpdated ? (
                                <li>{t('photo_prints.variant_changed')}</li>
                            ) : (
                                <>
                                    {failedUploadsCount > 0 && <li>{t('photo_prints.photos_errored', { count: failedUploadsCount })}</li>}
                                    {successfulUploadsCount > 0 && <li>{t('photo_prints.photos_uploaded', { count: successfulUploadsCount })}</li>}
                                    {isNewGroup && <li>{t('photo_prints.group_added')}</li>}
                                </>
                            )}
                        </ul>
                    </Cell>
                ) : (
                    <Cell className={classNames(styles.cell, styles.message)}>
                        <ButtonWithIcon
                            style="depressed"
                            onClick={removeMagicBorder}
                            icon="general-ui-eraser"
                            className={classNames(styles.removeBorderButton, { [styles.hiddenButton]: !hasMagicBorderApplied && !magicBorderIsApplied })}
                        >
                            {t('photo_prints.remove_magic_frame')}
                        </ButtonWithIcon>

                        <ButtonWithIcon
                            strokeWidth={2}
                            style="primary"
                            onClick={browse}
                            icon="alert-circle-plus"
                            className={classNames(styles.uploadButton, { [styles.hiddenButton]: isEmpty })}
                        >
                            {t('photo_prints.add_more')}
                        </ButtonWithIcon>

                        <ButtonWithIcon
                            strokeWidth={2}
                            style="depressed"
                            onClick={remove}
                            icon="general-ui-bin"
                            className={classNames(styles.removeButton, { [styles.hiddenButton]: !canRemove || !isVariantCollapsableOpen })}
                        >
                            {t('photo_prints.remove_group')}
                        </ButtonWithIcon>
                    </Cell>
                )}
            </Grid>
        </Panel>
    );
};

type OwnProps = PropsWithChildren<{
    groupId: string;
    browse: () => void;
    dragging: boolean;
    openAccessories: (accessoryId: string) => void;
}>;

type Props = OwnProps & {
    isEmpty: boolean;
    isUploading?: boolean;
    canRemove: boolean;
    activeVariant: PhotoPrintsJson.Variant;
    hasAccessoryApplied: boolean;
    remove: () => void;
    applyMagicBorder: (magicBorderId: string| null) => void;
    applyAccessory: (accessoryId: string) => void;
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => void;
    successfulUploadsCount: number;
    failedUploadsCount: number;
    isNewGroup: boolean;
    hasVariantUpdated: boolean;
    isPhotoFunProcessing: boolean;
    hasMagicBorderApplied: boolean;
    canHaveBorderDraggedInto?: boolean;
    selectPhotoIdsById: string[];
    hasValidAcccessoryQuantities?: boolean;
    getAccessoryValidation?: { minimumRequired: number; shortage: number; isValid: boolean };
    photos: PhotoPrintsJson.ProcessedPhoto[];
    frameId?: string;
    accessoryId?: string;
    hasActiveClickToApply: boolean;
    clearFrameId: () => void;
    clearAccessoryId: () => void;
    magicBorderAutoAppliedTimestamp?: number;
    borderOptions: VariantOption[];
};

const mapStateToProps = (state: PhotoPrintsPdpState, ownProps: OwnProps) => {

    const groupId = ownProps.groupId;
    const variant = selectVariantByGroupId(state, groupId);
    const size = selectSizeById(state, variant?.sizeId || '');

    const photos = Object.values(state.project.photos.entities).filter(
        (p): p is PhotoPrintsJson.ProcessedPhoto =>
            !!p && p.groupId === groupId && p.processed && p.status === 'saved'
    );

    const successfulUploads = selectSuccessfulUploadsCount(state, groupId);
    const failedUploads = selectFailedUploadsCount(state, groupId);

    return {
        isEmpty: selectIsEmptyGroup(state, groupId),
        activeVariant: selectActiveVariant(state, groupId),
        canRemove: selectCanRemoveGroup(state, groupId),
        successfulUploadsCount: successfulUploads,
        failedUploadsCount: failedUploads,
        isNewGroup: selectIsNewGroup(state, groupId),
        hasVariantUpdated: selectHasVariantUpdated(state, groupId),
        isPhotoFunProcessing: selectIsPhotoFunProcessing(state),
        canHaveBorderDraggedInto: selectCanHaveBorderDraggedInto(state),
        hasMagicBorderApplied: selectHasMagicBorderApplied(state, groupId),
        hasAccessoryApplied: size?.isAccessory ?? false,
        selectPhotoIdsById: selectPhotoIdsByGroupId(state, groupId),
        hasValidAcccessoryQuantities: hasValidAccessoryQuantityByGroupId(state, groupId),
        getAccessoryValidation: getAccessoryValidationByGroupId(state, groupId),
        photos,
        frameId: selectFrameId(state),
        accessoryId: selectAccessoryId(state),
        hasActiveClickToApply: !!(selectFrameId(state) || selectAccessoryId(state)),
        magicBorderAutoAppliedTimestamp: selectMagicBorderAutoAppliedTimestamp(state, groupId),
        borderOptions: selectBorderOptions(state, variant?.sizeId, variant?.paperId)
    };
};


const mapDispatchToProps = (dispatch: PhotoPrintsPdpDispatch, ownProps: OwnProps) => ({
    remove: () => dispatch(removeGroup(ownProps.groupId)),
    applyMagicBorder: (magicBorderId: string | null) => {
        dispatch(applyMagicBorderThunk(ownProps.groupId, magicBorderId));
    },
    applyAccessory: (accessoryId: string, sizeId?: string, borderId?: string) => {
        dispatch(applyAccessoryThunk({
            groupId: ownProps.groupId,
            accessoryId: accessoryId,
            sizeId: sizeId,
            borderId: borderId}));
    },
    updateVariant: (paperId: string | undefined, sizeId: string | undefined, borderId: string | undefined) => {
        dispatch(updateVariant(paperId, sizeId, ownProps.groupId, borderId));
    },
    clearFrameId: () => dispatch(setFrameId("")),
    clearAccessoryId: () => dispatch(setAccessoryId(""))
});

const mergeProps = (stateProps: ReturnType<typeof mapStateToProps>, dispatchProps: ReturnType<typeof mapDispatchToProps>, ownProps: OwnProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    applyAccessory: (accessoryId: string, sizeId?: string) => {
        dispatchProps.applyAccessory(accessoryId, sizeId, stateProps.activeVariant.borderId);
    }
});

export const PhotoPrintGroupPanel = connect(mapStateToProps, mapDispatchToProps, mergeProps)(_PhotoPrintGroupPanel);